import React, { useState, useCallback, useEffect, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import {
  saveGateway,
  removeGatewayAsync,
  updateGateway,
} from "../../store/gatewaysSlice";
import { setSelectedGateway, setCenter } from "../../store/uiSlice";
import {
  updateDevicesFromApi,
  recalculateDevicePositions,
} from "../../store/devicesSlice";
import { IGateway } from "../../types";
import CustomSlider from "./CustomSlider";
import { ApiService } from "../../services/api";
import { store } from "../../store";
import { selectCurrentMap, selectMapById } from "../../store/mapsSlice";
import GatewayPickerModal from "./GatewayPickerModal";

const GatewayManager: React.FC = () => {
  console.log("GatewayManager rendering");
  const dispatch = useDispatch<AppDispatch>();
  const { language, selectedGatewayMac } = useSelector(
    (state: RootState) => state.ui
  );
  const { entities: gateways, loading } = useSelector(
    (state: RootState) => state.gateways
  );
  const currentMap = useSelector(selectCurrentMap);
  console.log("GatewayManager currentMap:", currentMap);
  // Persist picker visibility across remounts so it doesn't auto-close in dev/StrictMode
  const [showGatewayPicker, setShowGatewayPicker] = useState<boolean>(
    () => (window as any).__showGatewayPicker || false
  );
  const [editingGateway, setEditingGateway] = useState<IGateway | null>(null);
  const [showOnlyCurrentMap, setShowOnlyCurrentMap] = useState(false);
  const [showRSSIConfig, setShowRSSIConfig] = useState<string | null>(null);

  // Local state for immediate UI updates and tracking unsaved changes
  const [localGatewayValues, setLocalGatewayValues] = useState<{
    [mac: string]: { logicThreshold?: number; viewRadius?: number };
  }>({});

  // Track which gateways have unsaved changes
  const [unsavedChanges, setUnsavedChanges] = useState<{
    [mac: string]: boolean;
  }>({});

  // Track saving state
  const [savingGateways, setSavingGateways] = useState<{
    [mac: string]: boolean;
  }>({});

  const isMountedRef = useRef(false);
  const hasLoadedRef = useRef(false);

  // Keep a global flag so picker state survives remounts
  useEffect(() => {
    (window as any).__showGatewayPicker = showGatewayPicker;
  }, [showGatewayPicker]);

  // Load checkbox state from localStorage on mount (only once)
  useEffect(() => {
    if (hasLoadedRef.current) return; // Prevent double loading in Strict Mode

    try {
      const savedState = localStorage.getItem(
        "gatewayManager_showOnlyCurrentMap"
      );
      if (savedState !== null) {
        const parsedState = JSON.parse(savedState);
        console.log("Loading gateway filter state:", parsedState);
        setShowOnlyCurrentMap(parsedState);
      } else {
        console.log("No saved gateway filter state found");
      }
    } catch (error) {
      console.error("Error loading gateway filter state:", error);
    }

    hasLoadedRef.current = true;
    isMountedRef.current = true;
  }, []);

  // Save checkbox state to localStorage when it changes
  useEffect(() => {
    if (isMountedRef.current && hasLoadedRef.current) {
      try {
        console.log("Saving gateway filter state:", showOnlyCurrentMap);
        localStorage.setItem(
          "gatewayManager_showOnlyCurrentMap",
          JSON.stringify(showOnlyCurrentMap)
        );
        console.log("Successfully saved gateway filter state");
      } catch (error) {
        console.error("Error saving gateway filter state:", error);
      }
    }
  }, [showOnlyCurrentMap]);

  const handleGatewayAdded = () => {
    setShowGatewayPicker(false);
  };

  const handleEditGateway = async (gateway: IGateway) => {
    if (editingGateway) {
      try {
        await dispatch(saveGateway(editingGateway));
        setEditingGateway(null);
      } catch (error) {
        console.error("Failed to update gateway:", error);
      }
    }
  };

  const handleDeleteGateway = async (mac: string) => {
    if (
      window.confirm(
        language === "he"
          ? "האם אתה בטוח שברצונך למחוק שער זה?"
          : "Are you sure you want to delete this gateway?"
      )
    ) {
      try {
        await dispatch(removeGatewayAsync(mac));
      } catch (error) {
        console.error("Failed to delete gateway:", error);
      }
    }
  };

  const handleSelectGateway = (gateway: IGateway) => {
    // Toggle selection - if already selected, deselect
    if (selectedGatewayMac === gateway.mac) {
      dispatch(setSelectedGateway(null));
    } else {
      // Select the gateway
      dispatch(setSelectedGateway(gateway.mac));

      // Center map on gateway
      dispatch(setCenter([gateway.position.y, gateway.position.x]));
    }
  };

  const handleRSSIChange = useCallback(
    (gateway: IGateway, newRSSI: number) => {
      console.log("🎯 RSSI Change triggered:", gateway.mac, newRSSI);

      // Update local state for UI
      setLocalGatewayValues((prev) => ({
        ...prev,
        [gateway.mac]: {
          ...prev[gateway.mac],
          logicThreshold: newRSSI,
        },
      }));

      // Mark as having unsaved changes
      setUnsavedChanges((prev) => ({
        ...prev,
        [gateway.mac]: true,
      }));

      // Update Redux state for immediate map updates
      const updatedGateway = {
        ...gateway,
        logicThreshold: newRSSI,
      };
      console.log(
        "⚡ Immediately updating Redux state for map:",
        updatedGateway
      );
      dispatch(updateGateway(updatedGateway));
    },
    [dispatch]
  );

  const handleViewRadiusChange = useCallback(
    (gateway: IGateway, newRadius: number) => {
      console.log("🔲 View Radius Change triggered:", gateway.mac, newRadius);

      // Update local state for UI
      setLocalGatewayValues((prev) => ({
        ...prev,
        [gateway.mac]: {
          ...prev[gateway.mac],
          viewRadius: newRadius,
        },
      }));

      // Mark as having unsaved changes
      setUnsavedChanges((prev) => ({
        ...prev,
        [gateway.mac]: true,
      }));

      // Update Redux state for immediate map updates
      const updatedGateway = {
        ...gateway,
        viewRadius: newRadius,
      };
      console.log(
        "⚡ Immediately updating Redux state for map:",
        updatedGateway
      );
      dispatch(updateGateway(updatedGateway));

      // Recalculate device positions immediately when viewRadius changes
      const currentState = store.getState();
      const currentGateways = currentState.gateways.entities;
      const currentConfiguredDevices = currentState.devices.configured || {};
      const existingDevices = currentState.devices.entities || {};

      // Create a mock API data to trigger position recalculation
      const mockApiData: any = {};
      Object.keys(existingDevices).forEach((mac) => {
        const device = existingDevices[mac];
        if (device.strongestGateway) {
          mockApiData[mac] = {
            mac,
            signal: {
              [device.strongestGateway]: {
                rssi: device.rssi || -60,
                received_at: new Date().toISOString(),
                ip: null,
              },
            },
            received: new Date().toISOString(),
          };
        }
      });

      // Recalculate positions with updated viewRadius
      const apiService = new ApiService(
        currentState.settings?.settings?.endpoint ||
          "http://localhost:3051/api/devices"
      );
      const mapDimensions = currentMap?.settings?.imageSize
        ? {
            width: currentMap.settings.imageSize.width,
            height: currentMap.settings.imageSize.height,
          }
        : { width: 800, height: 600 };

      const recalculatedDevices = apiService.processDevicesData(
        mockApiData,
        currentGateways,
        Object.values(currentConfiguredDevices),
        existingDevices,
        mapDimensions
      );

      console.log("🔄 Recalculating device positions due to viewRadius change");
      dispatch(recalculateDevicePositions(recalculatedDevices));
    },
    [dispatch, currentMap]
  );

  // Save gateway settings to backend
  const handleSaveGatewaySettings = useCallback(
    async (gateway: IGateway) => {
      const changes = localGatewayValues[gateway.mac];
      if (!changes || !unsavedChanges[gateway.mac]) {
        return; // No changes to save
      }

      if (!currentMap) {
        console.error(
          "❌ No current map selected, cannot save gateway settings"
        );
        return;
      }

      setSavingGateways((prev) => ({ ...prev, [gateway.mac]: true }));

      try {
        // Call the new API to update gateway settings
        const settingsApiService = new ApiService(
          process.env.REACT_APP_API_URL || "http://localhost:3051"
        );
        console.log(
          "🚀 Calling updateGatewaySettings:",
          gateway.mac,
          changes,
          "mapId:",
          currentMap._id
        );

        await settingsApiService.updateGatewaySettings(gateway.mac, {
          ...changes,
          mapId: currentMap._id,
        });
        console.log("✅ Gateway settings saved to backend successfully");

        // Mark as no longer having unsaved changes
        setUnsavedChanges((prev) => ({ ...prev, [gateway.mac]: false }));

        // Trigger device position recalculation if needed
        if (changes.logicThreshold !== undefined) {
          const currentState = store.getState();
          const currentGateways = currentState.gateways.entities;
          const currentDevices = currentState.devices.configured || {};

          // Create temporary API service to use positioning logic
          const apiService = new ApiService(
            "http://localhost:3051/api/devices"
          );

          try {
            // Fetch current device data and reprocess with updated gateway
            const apiData = await apiService.fetchDevices();
            const mapDimensions = currentMap?.settings?.imageSize
              ? {
                  width: currentMap.settings.imageSize.width,
                  height: currentMap.settings.imageSize.height,
                }
              : { width: 800, height: 600 };

            const processedDevices = apiService.processDevicesData(
              apiData,
              currentGateways,
              Object.values(currentDevices),
              undefined,
              mapDimensions
            );

            dispatch(
              updateDevicesFromApi({
                devices: processedDevices,
                gateways: currentGateways,
              })
            );
            console.log("✅ Device positions recalculated after RSSI change");
          } catch (deviceError) {
            console.error(
              "❌ Failed to recalculate device positions:",
              deviceError
            );
          }
        }
      } catch (error) {
        console.error("❌ Failed to save gateway settings:", error);
        // Could show a toast notification here
      } finally {
        setSavingGateways((prev) => ({ ...prev, [gateway.mac]: false }));
      }
    },
    [localGatewayValues, unsavedChanges, dispatch, currentMap]
  );

  // Cancel changes and revert to original values
  const handleCancelGatewaySettings = useCallback(
    (gateway: IGateway) => {
      // Remove local changes
      setLocalGatewayValues((prev) => {
        const newState = { ...prev };
        delete newState[gateway.mac];
        return newState;
      });

      // Mark as no longer having unsaved changes
      setUnsavedChanges((prev) => ({ ...prev, [gateway.mac]: false }));

      // Revert Redux state to original values
      dispatch(updateGateway(gateway));
      console.log("🔄 Reverted gateway settings for:", gateway.mac);
    },
    [dispatch]
  );

  const getRSSIQuality = (rssi: number) => {
    if (rssi >= -30)
      return {
        label: language === "he" ? "מעולה" : "Excellent",
        color: "text-green-600",
      };
    if (rssi >= -50)
      return {
        label: language === "he" ? "טוב" : "Good",
        color: "text-blue-600",
      };
    if (rssi >= -70)
      return {
        label: language === "he" ? "בינוני" : "Fair",
        color: "text-yellow-600",
      };
    return { label: language === "he" ? "חלש" : "Poor", color: "text-red-600" };
  };

  const getGatewayMapName = (gateway: IGateway) => {
    if (!gateway.mapId) {
      return language === "he" ? "לא משויך למפה" : "Not assigned to map";
    }

    const map = selectMapById(store.getState(), gateway.mapId);
    return map
      ? map.name
      : language === "he"
      ? "מפה לא נמצאה"
      : "Map not found";
  };

  const gatewayList = Object.values(gateways);
  const filteredGateways =
    showOnlyCurrentMap && currentMap
      ? gatewayList.filter((gateway) => gateway.mapId === currentMap._id)
      : gatewayList;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-medium text-gray-900">
            {language === "he" ? "ניהול שערים" : "Gateway Management"}
          </h3>
          <div className="flex items-center space-x-2 rtl:space-x-reverse">
            <label className="flex items-center space-x-2 rtl:space-x-reverse text-sm">
              <input
                type="checkbox"
                checked={showOnlyCurrentMap}
                onChange={(e) => {
                  console.log(
                    "Gateway checkbox onChange triggered:",
                    e.target.checked
                  );
                  setShowOnlyCurrentMap(e.target.checked);
                }}
                disabled={!currentMap}
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
              />
              <span className={currentMap ? "text-gray-700" : "text-gray-400"}>
                {language === "he"
                  ? "הצג רק מפה נוכחית"
                  : "Show only current map"}
              </span>
            </label>
            <button
              onClick={() => setShowGatewayPicker(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded-md text-sm font-medium transition-colors duration-200"
            >
              {language === "he" ? "הוסף שער" : "Add Gateway"}
            </button>
          </div>
        </div>

        {/* Gateway Count */}
        <p className="text-sm text-gray-600 mt-1">
          {language === "he"
            ? `${filteredGateways.length} שערים מוגדרים`
            : `${filteredGateways.length} configured gateways`}
        </p>
      </div>

      {/* Gateway Picker Modal */}
      {showGatewayPicker && (
        <GatewayPickerModal
          isOpen={showGatewayPicker}
          onClose={() => setShowGatewayPicker(false)}
          onGatewayAdded={handleGatewayAdded}
        />
      )}

      {/* Gateway List */}
      <div className="flex-1">
        {loading ? (
          <div className="p-4 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-600 mt-2">
              {language === "he" ? "טוען..." : "Loading..."}
            </p>
          </div>
        ) : filteredGateways.length === 0 ? (
          <div className="p-4 text-center text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8.111 16.404a5.5 5.5 0 017.778 0M12 20h.01m-7.08-7.071c3.904-3.905 10.236-3.905 14.141 0M1.394 9.393c5.857-5.857 15.355-5.857 21.213 0"
              />
            </svg>
            <p className="mt-2">
              {language === "he"
                ? showOnlyCurrentMap && currentMap
                  ? "אין שערים במפה הנוכחית"
                  : "אין שערים מוגדרים"
                : showOnlyCurrentMap && currentMap
                ? "No gateways on current map"
                : "No gateways configured"}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredGateways.map((gateway) => {
              const isEditing = editingGateway?.mac === gateway.mac;
              const isRSSIConfigOpen = showRSSIConfig === gateway.mac;
              const isSelected = selectedGatewayMac === gateway.mac;
              const rssiQuality = getRSSIQuality(gateway.maxRSSI || -50);

              return (
                <div
                  key={gateway.mac}
                  className={`p-4 cursor-pointer transition-colors duration-200 ${
                    isSelected
                      ? "bg-blue-50 border-l-4 border-blue-500"
                      : "hover:bg-gray-50"
                  }`}
                  onClick={() => handleSelectGateway(gateway)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      {isEditing ? (
                        <input
                          type="text"
                          value={editingGateway?.name || ""}
                          onChange={(e) =>
                            setEditingGateway({
                              ...gateway,
                              name: e.target.value,
                            })
                          }
                          className="font-medium text-gray-900 border-b border-blue-500 focus:outline-none bg-transparent"
                          onBlur={() => handleEditGateway(gateway)}
                          onKeyPress={(e) =>
                            e.key === "Enter" && handleEditGateway(gateway)
                          }
                          autoFocus
                        />
                      ) : (
                        <h4
                          className="font-medium text-gray-900 cursor-pointer hover:text-blue-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingGateway(gateway);
                          }}
                        >
                          {gateway.name}
                        </h4>
                      )}
                      <p className="text-sm text-gray-600">{gateway.mac}</p>
                      <div className="flex items-center space-x-4 rtl:space-x-reverse mt-1">
                        <span className="text-xs text-gray-500">
                          {language === "he" ? "מפה:" : "Map:"}{" "}
                          {getGatewayMapName(gateway)}
                        </span>
                        <span className="text-xs text-gray-500">
                          {language === "he" ? "רדיוס:" : "Radius:"}{" "}
                          {gateway.radius}px
                        </span>
                        <span
                          className={`text-xs font-medium ${rssiQuality.color}`}
                        >
                          RSSI: {gateway.maxRSSI || -50}dBm ({rssiQuality.label}
                          )
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2 rtl:space-x-reverse">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowRSSIConfig(
                            isRSSIConfigOpen ? null : gateway.mac
                          );
                        }}
                        className="text-blue-600 hover:text-blue-800 p-1"
                        title={
                          language === "he" ? "הגדר RSSI" : "Configure RSSI"
                        }
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 100 4m0-4v2m0-6V4"
                          />
                        </svg>
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteGateway(gateway.mac);
                        }}
                        className="text-red-600 hover:text-red-800 p-1"
                        title={language === "he" ? "מחק שער" : "Delete gateway"}
                      >
                        <svg
                          className="w-4 h-4"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* RSSI Configuration Panel */}
                  {isRSSIConfigOpen && (
                    <div
                      className="mt-3 p-3 bg-blue-50 rounded-md border border-blue-200"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <h5 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                        {language === "he"
                          ? "הגדרות שער"
                          : "Gateway Configuration"}
                        {unsavedChanges[gateway.mac] && (
                          <span className="ml-2 inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                            <div className="w-2 h-2 bg-orange-400 rounded-full mr-1 animate-pulse"></div>
                            {language === "he"
                              ? "שינויים לא נשמרו"
                              : "Unsaved Changes"}
                          </span>
                        )}
                      </h5>

                      {/* Logic Threshold (RSSI) */}
                      <div className="space-y-2 mb-4">
                        <label className="text-sm font-medium text-gray-700">
                          {language === "he"
                            ? "סף לוגי (RSSI)"
                            : "Logic Threshold (RSSI)"}
                        </label>
                        <div
                          className="flex items-center space-x-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CustomSlider
                            min={-120}
                            max={0}
                            value={
                              localGatewayValues[gateway.mac]?.logicThreshold ??
                              gateway.logicThreshold ??
                              gateway.maxRSSI ??
                              -50
                            }
                            onChange={(value) => {
                              console.log("CustomSlider RSSI onChange:", value);
                              handleRSSIChange(gateway, value);
                            }}
                            className="flex-1"
                            invertTrail={true}
                          />
                          <span className="text-sm font-medium text-gray-700 min-w-[60px]">
                            {localGatewayValues[gateway.mac]?.logicThreshold ??
                              gateway.logicThreshold ??
                              gateway.maxRSSI ??
                              -50}{" "}
                            dBm
                          </span>
                        </div>
                        <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRSSIChange(gateway, -45);
                            }}
                            className="px-2 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            -45 dBm
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRSSIChange(gateway, -75);
                            }}
                            className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                          >
                            -75 dBm
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRSSIChange(gateway, -120);
                            }}
                            className="px-2 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200"
                          >
                            -120 dBm
                          </button>
                        </div>
                      </div>

                      {/* View Radius */}
                      <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">
                          {language === "he"
                            ? "רדיוס כיסוי"
                            : "Coverage Radius"}
                        </label>
                        <div
                          className="flex items-center space-x-3"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <CustomSlider
                            min={0}
                            max={100}
                            value={
                              localGatewayValues[gateway.mac]?.viewRadius ??
                              gateway.viewRadius ??
                              gateway.radius ??
                              25
                            }
                            onChange={(value) => {
                              console.log(
                                "CustomSlider Coverage Radius onChange:",
                                value
                              );
                              handleViewRadiusChange(gateway, value);
                            }}
                            className="flex-1"
                          />
                          <span className="text-sm font-medium text-gray-700 min-w-[60px]">
                            {localGatewayValues[gateway.mac]?.viewRadius ??
                              gateway.viewRadius ??
                              gateway.radius ??
                              25}
                            %
                          </span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {language === "he"
                            ? "0 = כיסוי מינימלי (5% מהמפה), 100 = כיסוי מלא"
                            : "0 = minimal coverage (5% of map), 100 = full coverage"}
                        </div>
                        {/* <div className="flex space-x-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRadiusChange(gateway, 10);
                            }}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            {language === "he" ? "קטן" : "Small"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRadiusChange(gateway, 25);
                            }}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            {language === "he" ? "בינוני" : "Medium"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRadiusChange(gateway, 50);
                            }}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            {language === "he" ? "גדול" : "Large"}
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleViewRadiusChange(gateway, 100);
                            }}
                            className="px-2 py-1 text-xs bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
                          >
                            {language === "he" ? "מלא" : "Full"}
                          </button>
                        </div> */}
                      </div>

                      {/* Save/Cancel Buttons */}
                      {unsavedChanges[gateway.mac] && (
                        <div
                          className="flex space-x-2 pt-4 border-t border-gray-200"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            onClick={() => handleSaveGatewaySettings(gateway)}
                            disabled={savingGateways[gateway.mac]}
                            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
                              savingGateways[gateway.mac]
                                ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700 text-white"
                            }`}
                          >
                            {savingGateways[gateway.mac]
                              ? language === "he"
                                ? "שומר..."
                                : "Saving..."
                              : language === "he"
                              ? "שמור"
                              : "Save"}
                          </button>
                          <button
                            onClick={() => handleCancelGatewaySettings(gateway)}
                            disabled={savingGateways[gateway.mac]}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {language === "he" ? "בטל" : "Cancel"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default GatewayManager;
